import { useTranslation } from "react-i18next";
import camelCase from "lodash/camelCase";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";

import { EntityIcons } from "../../../../../utils/GlobalIcons";
import { Flex } from "../../../../../components/Flex";
import { Text } from "../../../../../components/Text";
import { useAppSelector } from "../../../../../redux/store";
import { reportSliceSelectors } from "../../../../../redux/report/report.selector";
import { entryMetricKeys, widgetTitleIconsAndColors } from "./types";
import { ThemeType } from "../../../../../theme/Theme";
import { Spacing } from "../../../../../theme/Spacing";
import {
  getRangeLabel,
  onViewEntitiesFromCards,
} from "../../../../../utils/Reports";
import { ReportKinds } from "../../../../../model/reports/types/ReportsKinds.enum";
import { EntryKind } from "../../../../../model/entry/types/EntryKind.enum";
import { ActivityEntityKind } from "../../../../../model/activity/types/ActivityEntityKind.enum";
import { ReportsSummarySection, SummaryMetric } from "./ReportsSummarySection";
import { ReportTrendChart } from "../../ReportTrendChart";
import { ReportRateTile } from "../../ReportRateTile";
import { ReportOriginList } from "../../ReportOriginList";

const OriginRow = styled(Flex)`
  align-items: center;
  justify-content: space-between;
  gap: ${Spacing.m};
  padding-block: 0.15rem;
`;

const OriginList = styled(Flex)`
  flex-direction: column;
  max-width: 28rem;
`;

export const ReportsSummaryEntryTab: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const reports = useAppSelector(reportSliceSelectors.selectReport);

  // Every entry has exactly one kind, so these really are parts of one whole.
  const kinds = entryMetricKeys.filter((k) => k !== ReportKinds.ENTRY);
  const rangeLabel = getRangeLabel(reports, t);
  const origins = reports.entriesByOrigin || [];

  const metrics: SummaryMetric[] = kinds.map((key) => {
    const kind = key.slice(key.indexOf("_") + 1) as EntryKind;

    return {
      key,
      title: t(`reports.${camelCase(key)}.title`),
      subtitle: `${t("reports.metric.createdInRange")} · ${rangeLabel}`,
      value: reports.metrics?.[key] ?? 0,
      previousValue: reports.previousMetrics?.[key],
      higherIsWorse: key === ReportKinds.ENTRY_JUNK,
      color: widgetTitleIconsAndColors[key].color as keyof ThemeType,
      icon: widgetTitleIconsAndColors[key].icon,
      onView: () =>
        onViewEntitiesFromCards(
          { range: reports?.range, scope: reports?.scope, kind },
          ReportKinds.ENTRY,
          navigate,
        ),
    };
  });

  return (
    <ReportsSummarySection
      title={t("common.entries")}
      icon={EntityIcons.Entries}
      info={t("reports.tabs.summary.subtab.entry.info")}
      metrics={metrics}
      pie
      columns={3}
      footer={
        <Flex flexDirection="column" gap="l">
          <ReportRateTile
            title={t("reports.rate.entryContactShare")}
            hint={t("reports.rate.entryContactShare.hint")}
            value={reports.rates?.entryContactShare ?? null}
            tone="contact"
          />

          <ReportOriginList origins={origins} />

          <ReportTrendChart kind={ActivityEntityKind.ENTRY} />
        </Flex>
      }
    />
  );
};
